import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { API_ROUTES, hasPermission, type MeResponse, type PermissionKey } from '@brochure/shared'
import { apiFetch, errorMessage } from '@/lib/api'
import { cn } from '@/lib/utils'
import { surfaceClass } from './styles'

type PermissionsState = {
  can: (key: PermissionKey) => boolean
  isLoading: boolean
  error?: unknown
  /** Da chiamare dopo aver modificato un gruppo: potrebbe essere il proprio. */
  reload: () => void
}

const PermissionsContext = createContext<PermissionsState | null>(null)

/**
 * Permessi dell'utente collegato (GET /api/me): decidono menu, pagine e pulsanti visibili.
 * È solo interfaccia: l'API ricontrolla ogni richiesta.
 */
export function PermissionsProvider({ children }: { children: ReactNode }) {
  const [permissions, setPermissions] = useState<PermissionKey[]>()
  const [error, setError] = useState<unknown>()
  const [reloads, setReloads] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    apiFetch<MeResponse>(API_ROUTES.me, { signal: controller.signal })
      .then((me) => {
        setPermissions(me.permissions)
        setError(undefined)
      })
      .catch((loadError: unknown) => {
        if (!controller.signal.aborted) setError(loadError)
      })
    return () => controller.abort()
  }, [reloads])

  const can = useCallback((key: PermissionKey) => hasPermission(permissions ?? [], key), [permissions])
  const reload = useCallback(() => setReloads((n) => n + 1), [])
  const value = useMemo(
    () => ({ can, isLoading: permissions === undefined && !error, error, reload }),
    [can, permissions, error, reload],
  )
  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
}

export function usePermissions(): PermissionsState {
  const state = useContext(PermissionsContext)
  if (!state) throw new Error('usePermissions fuori da PermissionsProvider')
  return state
}

/** Pagina dell'area riservata visibile solo con `permission` (tipicamente `<risorsa>.view`). */
export function RequirePermission({ permission, children }: { permission: PermissionKey; children: ReactNode }) {
  const { t } = useTranslation()
  const { can, isLoading, error } = usePermissions()
  if (isLoading) return null
  if (error || !can(permission)) {
    return (
      <p role="alert" className={cn(surfaceClass, 'p-6 text-sm text-ink')}>
        {error ? errorMessage(t, error) : t('admin.access.denied')}
      </p>
    )
  }
  return <>{children}</>
}
